import { useState, useEffect } from 'react';
import { Shuffle, Check } from 'lucide-react';
import { interestOptions, interestCategories } from './data';

interface InterestsSelectionProps {
  selectedInterests: string[];
  onToggleInterest: (interest: string) => void;
  onContinue: () => void;
}

export function InterestsSelection({ selectedInterests, onToggleInterest, onContinue }: InterestsSelectionProps) {
  const [displayedInterests, setDisplayedInterests] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Helper function to get a random set of interests
  const getRandomInterests = (count: number, mustInclude: string[] = []) => {
    const availableInterests = interestOptions.filter(i => !mustInclude.includes(i));
    const shuffled = [...availableInterests].sort(() => Math.random() - 0.5);
    const needed = Math.max(0, count - mustInclude.length);
    const randomPicks = shuffled.slice(0, needed);
    return [...mustInclude, ...randomPicks];
  };

  // Initialize displayed interests
  useEffect(() => {
    const displayed = getRandomInterests(20, selectedInterests);
    setDisplayedInterests(displayed);
  }, []);

  // Regenerate displayed interests while keeping selected ones
  const regenerateDisplayedInterests = () => {
    const displayed = getRandomInterests(20, selectedInterests);
    setDisplayedInterests(displayed);
    setSelectedCategory(null);
  };

  // Handle category selection
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    const categoryInterests = interestCategories[category];
    const combined = [...new Set([...selectedInterests, ...categoryInterests])];
    setDisplayedInterests(combined);
  };

  return (
    <div className="tw-space-y-3">
      <div className="tw-space-y-1">
        <h2 className="tw-text-xl tw-font-bold tw-text-gray-800">What are you interested in?</h2>
        <p className="tw-text-sm tw-text-gray-600">
          Select 3-5 interests. Browse by category or shuffle to see more options.
        </p>
      </div>

      <div className="tw-flex tw-justify-between tw-items-center">
        <p className="tw-text-xs tw-text-gray-600">Selected: {selectedInterests.length} / 5</p>
        <button
          onClick={regenerateDisplayedInterests}
          className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-lg tw-border-2 tw-border-gray-200 tw-bg-white tw-text-gray-800 tw-font-medium tw-transition-all hover:tw-border-gray-300"
        >
          <Shuffle size={14} />
          Shuffle Interests
        </button>
      </div>

      {/* Category Selection */}
      <div className="tw-space-y-1">
        <p className="tw-text-xs tw-font-medium tw-text-gray-700">Browse by category:</p>
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
          {Object.keys(interestCategories).map(category => (
            <button
              key={category}
              onClick={() => handleCategorySelect(category)}
              className={`tw-px-2 tw-py-1 tw-rounded-lg tw-text-xs tw-border-2 tw-transition-all ${
                selectedCategory === category
                  ? 'tw-border-blue-600 tw-bg-blue-50 tw-text-blue-600'
                  : 'tw-border-gray-200 tw-bg-white tw-text-gray-700 hover:tw-border-gray-300'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Interests Grid */}
      <div className="tw-grid tw-grid-cols-4 tw-gap-2">
        {displayedInterests.map(interest => {
          const isSelected = selectedInterests.includes(interest);

          return (
            <button
              key={interest}
              onClick={() => onToggleInterest(interest)}
              disabled={!isSelected && selectedInterests.length >= 5}
              className={`tw-p-2 tw-rounded-lg tw-border-2 tw-transition-all tw-text-center tw-relative ${
                isSelected
                  ? 'tw-border-blue-600 tw-bg-blue-50'
                  : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-300'
              } ${!isSelected && selectedInterests.length >= 5 ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
            >
              <span className={`tw-text-xs tw-font-medium ${isSelected ? 'tw-text-blue-600' : 'tw-text-gray-800'}`}>
                {interest}
              </span>
              {isSelected && <Check size={14} className="tw-absolute tw-top-1 tw-right-1 tw-text-blue-600" />}
            </button>
          );
        })}
      </div>

      <div className="tw-flex tw-justify-end tw-pt-2">
        <button
          type="button"
          onClick={onContinue}
          disabled={selectedInterests.length < 3}
          className="tw-rounded-full tw-px-6 tw-py-2 tw-text-sm tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          Generate Future Selves
        </button>
      </div>
    </div>
  );
}
